import React, { useState } from "react";
import { apiClient } from "../App";
import { Download, FileText, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { toast } from "sonner";

const RANGES = [
  { key: "last_30_days", label: "Last 30 days" },
  { key: "last_90_days", label: "Last 90 days" },
  { key: "last_6_months", label: "Last 6 months" },
  { key: "all_time", label: "All time" },
];

/**
 * LedgerDownloadButton
 *
 * Props:
 *  - userId:   string|null  → if provided, downloads that user's ledger (admin route).
 *                             If null/undefined, downloads the current user's own ledger.
 *  - userName: string|null  → used in toast / filename hint
 *  - variant:  "default" | "outline" | "secondary" | "ghost"
 *  - size:     "default" | "sm" | "lg" | "icon"
 *  - label:    optional custom button text
 *  - testIdPrefix: data-testid prefix (default "ledger-download")
 */
export const LedgerDownloadButton = ({
  userId = null,
  userName = "",
  variant = "outline",
  size = "default",
  label = "Download Ledger",
  testIdPrefix = "ledger-download",
  className = "",
}) => {
  const [loading, setLoading] = useState(false);

  const handleDownload = async (rangeKey, rangeLabel) => {
    if (loading) return;
    setLoading(true);
    try {
      const path = userId
        ? `/admin/users/${userId}/ledger/pdf?range=${rangeKey}`
        : `/me/ledger/pdf?range=${rangeKey}`;

      const response = await apiClient.get(path, { responseType: "blob" });

      // Extract filename from Content-Disposition if available
      let filename = `turf_tribe_ledger_${rangeKey}.pdf`;
      const cd = response.headers["content-disposition"];
      if (cd) {
        const m = cd.match(/filename="?([^"]+)"?/);
        if (m && m[1]) filename = m[1];
      }

      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);

      toast.success(
        userName
          ? `${userName}'s ledger (${rangeLabel}) downloaded`
          : `Your ledger (${rangeLabel}) downloaded`
      );
    } catch (err) {
      console.error("Ledger download failed:", err);
      toast.error("Failed to download ledger. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          disabled={loading}
          data-testid={`${testIdPrefix}-trigger`}
          className={className}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex items-center gap-2 text-[#064E3B]">
          <FileText className="w-4 h-4" />
          Download as PDF
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {RANGES.map((r) => (
          <DropdownMenuItem
            key={r.key}
            data-testid={`${testIdPrefix}-${r.key}`}
            onClick={() => handleDownload(r.key, r.label)}
            className="cursor-pointer"
          >
            {r.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LedgerDownloadButton;
